import { useState } from "react";
import { useApp } from "@/store/AppStore";
import { useAsesor } from "@/hooks/useAsesor";
import SectionTitle from "@/components/base/SectionTitle";
import { inputClass, labelClass, primaryButtonClass } from "@/components/base/fieldStyles";
import type { Client } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

// Registrar Venta a Cliente: el asesor vende desde su consignación o desde su
// mercancía propia. La venta se procesa con una función segura del backend.
const ChulearPage = () => {
  const { data, addClient, registerAdvisorSale } = useApp();
  const { items, margin } = useAsesor();

  const [selectedId, setSelectedId] = useState("");
  const [qty, setQty] = useState(1);
  const [source, setSource] = useState<"consignado" | "propio">("consignado");
  const [clientMode, setClientMode] = useState<"existente" | "nuevo">("existente");
  const [clientId, setClientId] = useState("");
  const [newName, setNewName] = useState("");
  const [newCedula, setNewCedula] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newDepartment, setNewDepartment] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newNeighborhood, setNewNeighborhood] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  const selected = items.find((i) => i.product.id === selectedId) ?? null;
  const available = selected
    ? source === "propio"
      ? selected.ownedUnits
      : selected.consignedUnits
    : 0;

  const reset = () => {
    setSelectedId("");
    setQty(1);
    setSource("consignado");
    setClientId("");
    setNewName("");
    setNewCedula("");
    setNewPhone("");
    setNewDepartment("");
    setNewCity("");
    setNewNeighborhood("");
    setNewAddress("");
  };

  const submit = async () => {
    setError("");
    if (!selected) {
      setError("Selecciona un producto para registrar la venta.");
      return;
    }
    const q = Number(qty) || 0;
    if (q <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }
    if (q > available) {
      setError(
        `Solo tienes ${available} unidades ${
          source === "propio" ? "propias" : "en consignación"
        }.`
      );
      return;
    }

    let resolvedId: string | null = null;
    let clientName = "Cliente mostrador";

    if (clientMode === "existente") {
      const c = data.clients.find((x) => x.id === clientId);
      if (!c) {
        setError("Selecciona un cliente o cambia a 'Nuevo cliente'.");
        return;
      }
      resolvedId = c.id;
      clientName = c.name;
    } else {
      if (!newName.trim()) {
        setError("Escribe el nombre del nuevo cliente.");
        return;
      }
      const saved = await addClient({
        id: "",
        name: newName.trim(),
        cedula: newCedula.trim(),
        phone: newPhone.trim(),
        department: newDepartment.trim(),
        city: newCity.trim(),
        neighborhood: newNeighborhood.trim(),
        address: newAddress.trim(),
      } as Client);
      if (!saved) {
        setError("No se pudo crear el cliente. Intenta de nuevo.");
        return;
      }
      resolvedId = saved.id;
      clientName = saved.name;
    }

    setBusy(true);
    const ok = await registerAdvisorSale({
      productId: selected.product.id,
      qty: q,
      source,
      clientId: resolvedId,
      clientName,
      pv: selected.product.pv * q,
      total: selected.product.pricePublic * q,
      marginPct: margin,
      unitCost: selected.costAdvisor,
    });
    setBusy(false);

    if (!ok) {
      setError("No se pudo registrar la venta. Revisa el stock e intenta de nuevo.");
      return;
    }

    setFeedback(`Venta a ${clientName} registrada. ¡Buen trabajo!`);
    reset();
    setTimeout(() => setFeedback(""), 2600);
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Registrar Venta a Cliente"
        subtitle="Marca tu venta: eliges el cliente (o creas uno nuevo), la venta se registra, se notifica a Juanka y tu stock baja automáticamente."
      />

      {feedback && (
        <div className="mb-4 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-300" />
          <p className="text-xs text-foreground-800">{feedback}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-archive-2-line text-accent-300" />
            <h3 className="editable-title text-base font-bold">Elige el producto vendido</h3>
          </div>

          {items.length === 0 ? (
            <div className="glass-panel rounded-lg px-5 py-10 text-center">
              <p className="text-sm text-foreground-700 font-semibold">
                No tienes productos disponibles
              </p>
              <p className="text-xs text-foreground-500 mt-1">
                Cuando recibas mercancía podrás registrar tus ventas aquí.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {items.map((i) => (
                <button
                  key={i.product.id}
                  onClick={() => {
                    setSelectedId(i.product.id);
                    setSource(i.consignedUnits > 0 ? "consignado" : "propio");
                  }}
                  className={`text-left glass-panel rounded-lg p-4 cursor-pointer transition-colors ${
                    selectedId === i.product.id
                      ? "border-accent-500/60 ring-2 ring-accent-500/25"
                      : "hover:border-accent-500/35"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground-950 truncate">{i.product.name}</p>
                      <p className="text-[11px] text-foreground-500">{i.product.sku}</p>
                    </div>
                    <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-secondary-500/20 text-secondary-100">
                      {formatNumber(i.units)} und
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px]">
                    <span className="text-foreground-600">Precio {formatCOP(i.pricePublic)}</span>
                    <span className="text-accent-200 font-semibold">+{formatCOP(i.profit)}/und</span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[10px] text-foreground-500">
                    <span>Consignado: {formatNumber(i.consignedUnits)}</span>
                    <span>Propio: {formatNumber(i.ownedUnits)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="glass-panel-strong rounded-lg p-5 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-md bg-primary-500/25 text-accent-200 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line" />
            </span>
            <h3 className="editable-title text-base font-bold">Detalle de la venta</h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className={labelClass}>Producto</label>
              <p className="text-sm text-foreground-950 font-semibold">
                {selected ? selected.product.name : "— selecciona uno —"}
              </p>
            </div>

            {selected && (
              <div>
                <label className={labelClass}>Vender desde</label>
                <div className="grid grid-cols-2 gap-2">
                  {(["consignado", "propio"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSource(s)}
                      className={`rounded-md border px-3 py-2 text-[11px] font-semibold cursor-pointer transition-colors ${
                        source === s
                          ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                          : "border-background-300/50 text-foreground-600 hover:border-accent-500/30"
                      }`}
                    >
                      {s === "consignado"
                        ? `Consignado (${formatNumber(selected.consignedUnits)})`
                        : `Propio (${formatNumber(selected.ownedUnits)})`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className={labelClass}>Cantidad vendida</label>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Number(e.target.value) || 0)}
                className={inputClass}
              />
            </div>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className={labelClass}>Cliente</label>
                <div className="flex items-center gap-1 bg-background-200/50 rounded-full p-1">
                  {(["existente", "nuevo"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setClientMode(m)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold cursor-pointer transition-colors ${
                        clientMode === m
                          ? "bg-accent-500/25 text-accent-200"
                          : "text-foreground-600 hover:text-accent-200"
                      }`}
                    >
                      {m === "existente" ? "Existente" : "Nuevo"}
                    </button>
                  ))}
                </div>
              </div>

              {clientMode === "existente" ? (
                <select
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className={inputClass}
                >
                  <option value="" className="bg-background-100">
                    Selecciona un cliente...
                  </option>
                  {data.clients.map((c) => (
                    <option key={c.id} value={c.id} className="bg-background-100">
                      {c.name} {c.cedula ? `· ${c.cedula}` : ""}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="space-y-2">
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Nombre completo"
                    className={inputClass}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={newCedula}
                      onChange={(e) => setNewCedula(e.target.value)}
                      placeholder="Cédula"
                      className={inputClass}
                    />
                    <input
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="Celular"
                      className={inputClass}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={newDepartment}
                      onChange={(e) => setNewDepartment(e.target.value)}
                      placeholder="Departamento (DTO)"
                      className={inputClass}
                    />
                    <input
                      value={newCity}
                      onChange={(e) => setNewCity(e.target.value)}
                      placeholder="Ciudad / Municipio"
                      className={inputClass}
                    />
                  </div>
                  <input
                    value={newNeighborhood}
                    onChange={(e) => setNewNeighborhood(e.target.value)}
                    placeholder="Barrio"
                    className={inputClass}
                  />
                  <input
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    placeholder="Agregar indicaciones (torre, manzana, casa...)"
                    className={inputClass}
                  />
                </div>
              )}
            </div>

            <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-3">
              <div className="flex items-center justify-between text-xs text-foreground-600">
                <span>Total a cobrar</span>
                <span className="text-base font-bold text-foreground-950">
                  {formatCOP(selected ? selected.pricePublic * (Number(qty) || 0) : 0)}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-foreground-600 mt-1">
                <span>Tu ganancia</span>
                <span className="font-semibold text-accent-200">
                  {formatCOP(selected ? selected.profit * (Number(qty) || 0) : 0)}
                </span>
              </div>
            </div>

            {error && (
              <p className="text-[11px] text-primary-200 flex items-center gap-1.5">
                <i className="ri-error-warning-line" /> {error}
              </p>
            )}

            <button
              onClick={submit}
              disabled={busy}
              className={`${primaryButtonClass} w-full ${busy ? "opacity-60" : ""}`}
            >
              <i className="ri-checkbox-circle-line" /> {busy ? "Registrando..." : "Registrar venta"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChulearPage;